import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BlockList } from '@/components/marketing/templates/BlockList';
import { TemplateList } from '@/components/marketing/templates/TemplateList';
import { VariablesManager } from '@/components/marketing/templates/VariablesManager';
import { useAuth } from '@/lib/use-auth';
import { EmptyState, PageHeader, Panel } from '@/components/ui';
import { ViewToggle } from '@/components/ui/ViewToggle';

/*
 * `/marketing/templates` — three tabs: Templates (default), Blocks, Variables.
 *
 * The tab lives in `?tab=`, and the default is spelled by the key's ABSENCE:
 * nothing is written on arrival, and switching back to Templates deletes the
 * key. Exactly one tab mounts, so each fetches only when it is shown.
 *
 * The route carries no guard, so a reader without `marketing-templates.read`
 * meets the refusal below and the page issues no request at all.
 */

type TemplatesTab = 'templates' | 'blocks' | 'variables';

const TABS: readonly { value: TemplatesTab; label: string }[] = [
  { value: 'templates', label: 'Templates' },
  { value: 'blocks', label: 'Blocks' },
  { value: 'variables', label: 'Variables' },
];

const TAB_PARAM = 'tab';

/** A positive test with a fallback — an unrecognised value lands on Templates. */
function parseTab(raw: string | null): TemplatesTab {
  if (raw === 'blocks') return 'blocks';
  if (raw === 'variables') return 'variables';
  return 'templates';
}

function TemplatesTabs({ value, onChange }: { value: TemplatesTab; onChange: (next: TemplatesTab) => void }) {
  return <ViewToggle label="Templates section" value={value} options={TABS} onChange={onChange} />;
}

export function TemplatesPage() {
  const { ability } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get(TAB_PARAM));
  const canRead = ability.canRead('marketing-templates');

  const changeTab = useCallback(
    (next: TemplatesTab) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'templates') params.delete(TAB_PARAM);
      else params.set(TAB_PARAM, next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  if (!canRead) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B Marketing" title="Templates" />
        <Panel>
          <EmptyState
            title="Templates are restricted"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader eyebrow="B2B Marketing" title="Templates" />
      <div className="flex flex-wrap items-center gap-2">
        <TemplatesTabs value={tab} onChange={changeTab} />
      </div>
      {tab === 'templates' && <TemplateList />}
      {tab === 'blocks' && <BlockList />}
      {tab === 'variables' && <VariablesManager />}
    </div>
  );
}
